"use client";

import Image from "next/image";
import { motion } from "motion/react";
import FadeIn from "@/components/fade-in";

const stories = [
  {
    src: "/reconnect/1.jpg",
    alt: "Reconnect cabins in evening light",
    title: "Slow rhythm, clear focus",
    copy: "A premium setting where leaders step out of constant urgency and return to lucid decisions.",
    className: "lg:col-span-7",
  },
  {
    src: "/reconnect/2.jpg",
    alt: "Individual cabin and green surroundings",
    title: "Real personal space",
    copy: "Individual cabins for reflection and integration between therapeutic work sessions.",
    className: "lg:col-span-5",
  },
  {
    src: "/reconnect/3.jpg",
    alt: "Outdoor terrace at glamping",
    title: "Comfort without noise",
    copy: "High comfort, away from the urban rhythm. The context becomes part of the process.",
    className: "lg:col-span-5",
  },
  {
    src: "/reconnect/4.jpg",
    alt: "Reconnect Glamping exterior",
    title: "A place that supports authentic conversations",
    copy: "Small group, intimacy and psychological safety for discussions that matter.",
    className: "lg:col-span-7",
  },
];

export default function ReMindPhotoNarrativeSectionEn() {
  return (
    <section className="relative bg-[#f6f2eb] py-20 text-[#1e352f] sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <FadeIn className="mb-14 text-center">
          <p className="mb-4 text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-[#2d6158]/70">The Experience Setting</p>
          <h2 className="text-3xl font-semibold sm:text-4xl">A space that looks beautiful and works in favour of the process.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-[#2d4e47]/74">
            Not aesthetics for its own sake. Every environmental detail is designed for clarity, presence and the recovery of leadership energy.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          {stories.map((story, i) => (
            <FadeIn key={story.src} delay={i * 90} className={story.className}>
              <article className="group relative overflow-hidden rounded-[1.75rem] border border-white/70 bg-[#efe9de] shadow-[0_16px_50px_rgba(17,35,31,0.08)]">
                <div className="relative h-[280px] sm:h-[330px]">
                  <motion.div
                    className="absolute inset-0"
                    initial={{ scale: 1.08 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{
                      duration: 1.1,
                      delay: i * 0.08,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                  >
                    <Image
                      src={story.src}
                      alt={story.alt}
                      fill
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      className="object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                  </motion.div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/58 via-black/18 to-transparent" />
                </div>

                <div className="absolute inset-x-0 bottom-0 p-6 text-white sm:p-7">
                  <h3 className="text-2xl font-semibold tracking-tight">{story.title}</h3>
                  <p className="mt-2 max-w-lg text-sm leading-relaxed text-white/84">{story.copy}</p>
                </div>
              </article>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}
